import { Button, Form, InputNumber, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ConnectorStandard, PowerType } from '@goelectric/shared-types';
import type { ConnectorFormRow, StationFormState } from '../utils/stationForm';
import { connectorPowerRule, connectorUniqueIdRule } from '../utils/stationForm';
import {
  CONNECTOR_STANDARD_OPTIONS,
  POWER_TYPE_OPTIONS,
} from '../utils/format';

/**
 * Paso 3 del alta de Cargador: editor de conectores (`Form.List`). Cada fila lleva su Nº de
 * conector (entero ≥ 1, único dentro del cargador), estándar (Tipo 2 / CCS2), tipo de
 * potencia, potencia requerida y voltaje/amperaje opcionales.
 *
 * El contrato exige **al menos un conector** (`minItems: 1`): se valida con una `rule` sobre
 * el `Form.List`. La potencia de cada conector no puede superar la del gabinete
 * (`maxPowerKw`), regla que el backend revalida (`EQUIPO_POTENCIA_INVALIDA`).
 */
export function StationConnectorsFields() {
  const form = Form.useFormInstance<StationFormState>();

  return (
    <section>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Conectores
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Registra los conectores de este nuevo cargador
      </Typography.Paragraph>

      <Form.List
        name="connectors"
        rules={[
          {
            validator: async (_rule, value: ConnectorFormRow[] | undefined) => {
              if (!value || value.length < 1) {
                throw new Error('Debe registrarse al menos un conector');
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Space
                key={field.key}
                className="cargadores-connectors__row"
                wrap
                // ponytail: anchos fijos por columna (ver `width` en cada `Form.Item`). antd
                // dimensiona la columna por el más ancho entre label y control, así que en la
                // fila 0 los labels "Potencia (kW)" y "Amperaje (A)" estiraban su columna y
                // corrían los campos siguientes. Cada ancho es el mayor entre el control vigente
                // y el label real (Inter 600 @16px + el `*` de requerido + los márgenes del `:`
                // oculto que antd reserva). Los controles llenan la columna (`width: '100%'`)
                // para que el gutter entre columnas sea parejo.
                // `start`: los controles quedan anclados arriba. Así un mensaje de validación
                // (que alarga sólo su columna) no corre el control de las filas vecinas.
                align="start"
                style={{ display: 'flex', marginBottom: 8 }}
              >
                <Form.Item
                  label={index === 0 ? 'Nº' : ' '}
                  name={[field.name, 'id']}
                  rules={[
                    { required: true, message: 'Ingresa el número del conector.' },
                    { type: 'number', min: 1, message: 'El número debe ser al menos 1.' },
                    connectorUniqueIdRule(() => {
                      const rows =
                        (form.getFieldValue('connectors') as ConnectorFormRow[] | undefined) ?? [];
                      return rows
                        .filter((_row, rowIndex) => rowIndex !== index)
                        .map((row) => row?.id);
                    }),
                  ]}
                  style={{ marginBottom: 0, width: 80 }}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    style={{ width: '100%' }}
                    placeholder="1"
                    aria-label={`Número del conector ${index + 1}`}
                  />
                </Form.Item>

                <Form.Item
                  label={index === 0 ? 'Estándar' : ' '}
                  name={[field.name, 'standard']}
                  rules={[{ required: true, message: 'Selecciona el estándar.' }]}
                  style={{ marginBottom: 0, width: 120 }}
                >
                  <Select<ConnectorStandard>
                    placeholder="Estándar"
                    options={CONNECTOR_STANDARD_OPTIONS}
                    style={{ width: '100%' }}
                    aria-label={`Estándar del conector ${index + 1}`}
                  />
                </Form.Item>

                <Form.Item
                  label={index === 0 ? 'Tipo de potencia' : ' '}
                  name={[field.name, 'powerType']}
                  style={{ marginBottom: 0, width: 150 }}
                >
                  <Select<PowerType>
                    allowClear
                    placeholder="Tipo"
                    options={POWER_TYPE_OPTIONS}
                    style={{ width: '100%' }}
                    aria-label={`Tipo de potencia del conector ${index + 1}`}
                  />
                </Form.Item>

                <Form.Item
                  label={index === 0 ? 'Potencia (kW)' : ' '}
                  name={[field.name, 'maxPowerKw']}
                  rules={[
                    { required: true, message: 'Ingresa la potencia.' },
                    connectorPowerRule(() => form.getFieldValue('maxPowerKw') as number | undefined),
                  ]}
                  style={{ marginBottom: 0, width: 140 }}
                >
                  <InputNumber
                    min={0.01}
                    step={1}
                    style={{ width: '100%' }}
                    placeholder="22"
                    aria-label={`Potencia del conector ${index + 1}`}
                  />
                </Form.Item>

                <Form.Item
                  label={index === 0 ? 'Voltaje (V)' : ' '}
                  name={[field.name, 'maxVoltageV']}
                  style={{ marginBottom: 0, width: 100 }}
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    style={{ width: '100%' }}
                    placeholder="Opcional"
                    aria-label={`Voltaje del conector ${index + 1}`}
                  />
                </Form.Item>

                <Form.Item
                  label={index === 0 ? 'Amperaje (A)' : ' '}
                  name={[field.name, 'maxAmperageA']}
                  style={{ marginBottom: 0, width: 120 }}
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    style={{ width: '100%' }}
                    placeholder="Opcional"
                    aria-label={`Amperaje del conector ${index + 1}`}
                  />
                </Form.Item>

                {/* Columna de acción: en TODAS las filas el `Form.Item` reserva el alto del
                    label (el encabezado —Nº, Estándar, …— vive en la fila 0), así el botón
                    queda a la altura del control y no de la fila entera. */}
                <Form.Item label=" " style={{ marginBottom: 0, width: 44 }}>
                  {index === 0 ? (
                    /* La fila 0 SIEMPRE existe (el paso exige ≥ 1 conector): no lleva trash.
                       El spacer conserva el ancho de la columna que el botón ocuparía
                       (`controlHeight` del design system = ancho del Button icon-only de antd)
                       para que todas las filas midan igual y envuelvan igual. */
                    <span aria-hidden style={{ display: 'inline-block', width: 44 }} />
                  ) : (
                    /* El condicional es sobre `index`, no `field.name`, para sobrevivir al
                       reindexado de `Form.List` tras borrar una fila intermedia. */
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`Quitar conector ${index + 1}`}
                      onClick={() => remove(field.name)}
                    />
                  )}
                </Form.Item>
              </Space>
            ))}

            <Form.ErrorList errors={errors} />

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                Agregar conector
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </section>
  );
}
